import React, { useState, useEffect } from "react";
import Modal from "react-modal";
import Swal from "sweetalert2";
import axiosInstance from "../../config/axiosConfig";
import Actions from "../../components/courses/Actions";
import { CircularProgress } from "@mui/material";
import { MAX_SIZE_MB } from "../../helper";

const customModalStyles = {
  content: {
    padding: "24px",
    width: "90%",
    maxWidth: "640px",
    margin: "auto",
    borderRadius: "12px",
    boxShadow: "0 10px 25px rgba(0, 0, 0, 0.15)",
    maxHeight: "90vh",
    overflowY: "auto",
  },
};

const isImageFile = (url = "") =>
  /\.(jpg|jpeg|png|webp|gif|bmp|svg)(\?|$)/i.test(String(url).split("?")[0] || "");

const FilePreview = ({ url, alt, style }) => {
  if (!url) return null;

  if (isImageFile(url)) {
    return (
      <img
        src={url}
        alt={alt || "Generic Ad Preview"}
        style={{ ...style, objectFit: "cover", borderRadius: "6px" }}
      />
    );
  }

  const label = /\.pdf(\?|$)/i.test(url) ? "PDF" : "DOC";

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        ...style,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        background: "#f0f4f8",
        color: "#1e293b",
        textDecoration: "none",
        border: "1px solid #cbd5e1",
        borderRadius: "6px",
        padding: "10px",
        boxSizing: "border-box",
      }}
    >
      <span style={{ fontSize: 24, fontWeight: 700, color: "#2563eb" }}>{label}</span>
      <span style={{ fontSize: 12, marginTop: 4, color: "#475569" }}>Open / Download</span>
    </a>
  );
};

export default function GenericAd() {
  const [genericAds, setGenericAds] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editAdId, setEditAdId] = useState(null);
  const [adsFetchLoading, setAdsFetchLoading] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [sortByLatest, setSortByLatest] = useState(true);
  const [adFile, setAdFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);

  const initialFormData = {
    title: "",
    category: "General Notice",
    date: "",
    expiryDate: "",
    description: "",
    display: "yes",
    file: null,
  };

  const [formData, setFormData] = useState(initialFormData);

  useEffect(() => {
    fetchGenericAds();
  }, []);

  const fetchGenericAds = async () => {
    setAdsFetchLoading(true);
    try {
      const response = await axiosInstance.get("generic-ad/all");
      setGenericAds(response?.data?.data || []);
    } catch (error) {
      console.error("Error fetching generic ads:", error);
    }
    setAdsFetchLoading(false);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const fileSizeInMB = file.size / (1024 * 1024);
      if (fileSizeInMB > (MAX_SIZE_MB || 15)) {
        Swal.fire(
          "File Too Large",
          `File size exceeds ${MAX_SIZE_MB || 15} MB. Please select a smaller file.`,
          "warning"
        );
        e.target.value = "";
        return;
      }
      setAdFile(file);
      if (file.type.startsWith("image/")) {
        setFilePreview(URL.createObjectURL(file));
      } else {
        setFilePreview(null);
      }
    }
  };

  const handleCreateAd = async (e) => {
    e.preventDefault();
    setButtonLoading(true);

    const formDataToSend = new FormData();
    Object.keys(formData).forEach((key) => {
      if (key === "file") return;
      if (formData[key] !== null && formData[key] !== undefined) {
        formDataToSend.append(key, formData[key]);
      }
    });

    if (adFile) {
      formDataToSend.append("file", adFile);
    }

    try {
      await axiosInstance.post("generic-ad/create", formDataToSend);
      fetchGenericAds();
      setShowCreateModal(false);
      setFormData(initialFormData);
      setAdFile(null);
      setFilePreview(null);
      Swal.fire("Success!", "Advertisement created successfully!", "success");
    } catch (error) {
      console.error("Error creating generic ad:", error);
      const msg = error.response?.data?.message || "Failed to create ad";
      Swal.fire("Error", msg, "error");
    }
    setButtonLoading(false);
  };

  const handleViewAd = async (id) => {
    try {
      setEditAdId(id);
      setShowEditModal(true);
      const res = await axiosInstance.get(`generic-ad/single/${id}`);
      const data = res?.data?.data;
      if (data) {
        setFormData({
          title: data.title || "",
          category: data.category || "General Notice",
          date: data.date || "",
          expiryDate: data.expiryDate || "",
          description: data.description || "",
          display: data.display || "yes",
          file: data.file || null,
        });
        if (data.file && isImageFile(data.file)) {
          setFilePreview(data.file);
        } else {
          setFilePreview(null);
        }
      }
    } catch (error) {
      console.error("Error fetching ad:", error);
      Swal.fire("Error", "Failed to fetch ad details.", "error");
    }
  };

  const handleEditAd = async (e) => {
    e.preventDefault();
    setButtonLoading(true);

    const formDataToSend = new FormData();
    Object.keys(formData).forEach((key) => {
      if (key === "file") return;
      if (formData[key] !== null && formData[key] !== undefined) {
        formDataToSend.append(key, formData[key]);
      }
    });

    if (adFile) {
      formDataToSend.append("file", adFile);
    }
    formDataToSend.append("_method", "PUT");

    try {
      await axiosInstance.post(`generic-ad/change/${editAdId}`, formDataToSend);
      fetchGenericAds();
      setShowEditModal(false);
      setAdFile(null);
      setFilePreview(null);
      Swal.fire("Success!", "Advertisement updated successfully!", "success");
    } catch (error) {
      console.error("Error updating ad:", error);
      const msg = error.response?.data?.message || "Failed to update ad";
      Swal.fire("Error", msg, "error");
    }
    setButtonLoading(false);
  };

  const handleDeleteAd = async (id) => {
    try {
      const result = await Swal.fire({
        title: "Delete Advertisement?",
        text: "This action cannot be undone.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#dc2626",
        cancelButtonColor: "#64748b",
        confirmButtonText: "Yes, delete it!",
      });

      if (result.isConfirmed) {
        await axiosInstance.delete(`generic-ad/delete/${id}`);
        fetchGenericAds();
        Swal.fire("Deleted!", "Advertisement has been removed.", "success");
      }
    } catch (error) {
      console.error("Error deleting ad:", error);
      Swal.fire("Error", "Failed to delete ad", "error");
    }
  };

  const sortedAds = sortByLatest
    ? [...genericAds].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    : genericAds;

  return (
    <div style={{ padding: "20px" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "24px",
          flexWrap: "wrap",
          gap: "12px",
        }}
      >
        <div>
          <h1 style={{ margin: 0, fontSize: "24px", fontWeight: "700", color: "#1e293b" }}>
            Generic Ads & Notices
          </h1>
          <p style={{ margin: "4px 0 0", color: "#64748b", fontSize: "14px" }}>
            Manage general ads, public notices, press releases, auctions, and banners
          </p>
        </div>

        <div style={{ display: "flex", gap: "10px" }}>
          <button
            onClick={() => setSortByLatest((prev) => !prev)}
            style={{
              padding: "10px 16px",
              backgroundColor: sortByLatest ? "#3b82f6" : "#f1f5f9",
              color: sortByLatest ? "#fff" : "#334155",
              border: "1px solid #cbd5e1",
              borderRadius: "6px",
              cursor: "pointer",
              fontWeight: 500,
            }}
          >
            {sortByLatest ? "Latest First" : "Default Order"}
          </button>
          <button
            onClick={() => {
              setFormData(initialFormData);
              setAdFile(null);
              setFilePreview(null);
              setShowCreateModal(true);
            }}
            style={{
              padding: "10px 20px",
              backgroundColor: "#16a34a",
              color: "#fff",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            + Add New Ad
          </button>
        </div>
      </div>

      {adsFetchLoading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: "60px 0" }}>
          <CircularProgress />
        </div>
      ) : sortedAds.length === 0 ? (
        <div
          style={{
            textAlign: "center",
            padding: "48px 20px",
            background: "#fff",
            borderRadius: "8px",
            border: "1px dashed #cbd5e1",
          }}
        >
          <p style={{ color: "#64748b", fontSize: "16px", margin: 0 }}>
            No generic ads found. Click <strong>"+ Add New Ad"</strong> to create one.
          </p>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: "20px" }}>
          {sortedAds.map((ad) => (
            <div
              key={ad._id}
              style={{
                backgroundColor: "#fff",
                borderRadius: "10px",
                padding: "16px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                border: "1px solid #e2e8f0",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              <div>
                {ad.file && (
                  <div style={{ marginBottom: "12px" }}>
                    <FilePreview url={ad.file} alt={ad.title} style={{ width: "100%", height: "160px" }} />
                  </div>
                )}

                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
                  <span
                    style={{
                      fontSize: "11px",
                      fontWeight: 700,
                      color: "#4338ca",
                      backgroundColor: "#e0e7ff",
                      padding: "2px 8px",
                      borderRadius: "4px",
                      textTransform: "uppercase",
                    }}
                  >
                    {ad.category || "General Notice"}
                  </span>
                  <span
                    style={{
                      fontSize: "11px",
                      color: ad.display === "no" ? "#dc2626" : "#16a34a",
                      fontWeight: 600,
                    }}
                  >
                    {ad.display === "no" ? "Hidden" : "Active"}
                  </span>
                </div>

                <h3 style={{ fontSize: "16px", fontWeight: "700", color: "#0f172a", margin: "6px 0" }}>
                  {ad.title}
                </h3>

                {ad.description && (
                  <p
                    style={{
                      fontSize: "13px",
                      color: "#475569",
                      margin: "0 0 10px",
                      lineHeight: "1.4",
                      maxHeight: "50px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {ad.description}
                  </p>
                )}

                <div style={{ fontSize: "12px", color: "#64748b", display: "flex", flexDirection: "column", gap: "2px" }}>
                  {ad.date && <div><strong>Date:</strong> {ad.date}</div>}
                  {ad.expiryDate && <div><strong>Expiry:</strong> {ad.expiryDate}</div>}
                </div>
              </div>

              <div style={{ borderTop: "1px solid #f1f5f9", paddingTop: "12px", marginTop: "12px" }}>
                <Actions
                  courseId={ad._id}
                  onEdit={handleViewAd}
                  onDelete={handleDeleteAd}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Modal */}
      <Modal
        isOpen={showCreateModal}
        onRequestClose={() => setShowCreateModal(false)}
        style={customModalStyles}
        contentLabel="Create New Advertisement"
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
          <h2 style={{ margin: 0, fontSize: "20px", fontWeight: "700", color: "#0f172a" }}>Add Generic Ad / Notice</h2>
          <button
            onClick={() => setShowCreateModal(false)}
            style={{ background: "none", border: "none", fontSize: "20px", cursor: "pointer", color: "#64748b" }}
          >
            &times;
          </button>
        </div>

        <form onSubmit={handleCreateAd}>
          <div style={{ marginBottom: "12px" }}>
            <label style={{ display: "block", fontWeight: "600", fontSize: "13px", marginBottom: "4px" }}>
              Ad Title *
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
              placeholder="e.g. Expression of Interest for Campus Canteen"
              style={{ width: "100%", padding: "8px 12px", borderRadius: "6px", border: "1px solid #cbd5e1", boxSizing: "border-box" }}
            />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "12px" }}>
            <div>
              <label style={{ display: "block", fontWeight: "600", fontSize: "13px", marginBottom: "4px" }}>
                Category
              </label>
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                style={{ width: "100%", padding: "8px 12px", borderRadius: "6px", border: "1px solid #cbd5e1", boxSizing: "border-box" }}
              >
                <option value="General Notice">General Notice</option>
                <option value="Auctions">Auctions</option>
                <option value="Expressions of Interest">Expressions of Interest</option>
                <option value="Public Notices">Public Notices</option>
                <option value="Recruitment Notice">Recruitment Notice</option>
                <option value="Workshop / Conference">Workshop / Conference</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div>
              <label style={{ display: "block", fontWeight: "600", fontSize: "13px", marginBottom: "4px" }}>
                Display Status
              </label>
              <select
                name="display"
                value={formData.display}
                onChange={handleChange}
                style={{ width: "100%", padding: "8px 12px", borderRadius: "6px", border: "1px solid #cbd5e1", boxSizing: "border-box" }}
              >
                <option value="yes">Visible / Active</option>
                <option value="no">Hidden</option>
              </select>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "12px" }}>
            <div>
              <label style={{ display: "block", fontWeight: "600", fontSize: "13px", marginBottom: "4px" }}>
                Date
              </label>
              <input
                type="date"
                name="date"
                value={formData.date}
                onChange={handleChange}
                style={{ width: "100%", padding: "8px 12px", borderRadius: "6px", border: "1px solid #cbd5e1", boxSizing: "border-box" }}
              />
            </div>

            <div>
              <label style={{ display: "block", fontWeight: "600", fontSize: "13px", marginBottom: "4px" }}>
                Expiry / Last Date
              </label>
              <input
                type="date"
                name="expiryDate"
                value={formData.expiryDate}
                onChange={handleChange}
                style={{ width: "100%", padding: "8px 12px", borderRadius: "6px", border: "1px solid #cbd5e1", boxSizing: "border-box" }}
              />
            </div>
          </div>

          <div style={{ marginBottom: "12px" }}>
            <label style={{ display: "block", fontWeight: "600", fontSize: "13px", marginBottom: "4px" }}>
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={3}
              placeholder="Provide brief details or overview of this advertisement..."
              style={{ width: "100%", padding: "8px 12px", borderRadius: "6px", border: "1px solid #cbd5e1", boxSizing: "border-box" }}
            />
          </div>

          <div style={{ marginBottom: "16px" }}>
            <label style={{ display: "block", fontWeight: "600", fontSize: "13px", marginBottom: "4px" }}>
              Upload Image / PDF Ad
            </label>
            <input
              type="file"
              onChange={handleFileChange}
              accept=".pdf,.jpg,.jpeg,.png,.webp"
              style={{ width: "100%", padding: "8px 12px", borderRadius: "6px", border: "1px solid #cbd5e1", boxSizing: "border-box" }}
            />
            {filePreview && (
              <img
                src={filePreview}
                alt="Preview"
                style={{ width: "140px", height: "auto", marginTop: "8px", borderRadius: "4px" }}
              />
            )}
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
            <button
              type="button"
              onClick={() => setShowCreateModal(false)}
              style={{ padding: "10px 16px", background: "#f1f5f9", color: "#334155", border: "1px solid #cbd5e1", borderRadius: "6px", cursor: "pointer" }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={buttonLoading}
              style={{ padding: "10px 24px", background: "#16a34a", color: "#fff", border: "none", borderRadius: "6px", cursor: "pointer", fontWeight: 600 }}
            >
              {buttonLoading ? <CircularProgress size={20} color="inherit" /> : "Publish Ad"}
            </button>
          </div>
        </form>
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={showEditModal}
        onRequestClose={() => setShowEditModal(false)}
        style={customModalStyles}
        contentLabel="Edit Advertisement"
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
          <h2 style={{ margin: 0, fontSize: "20px", fontWeight: "700", color: "#0f172a" }}>Edit Advertisement</h2>
          <button
            onClick={() => setShowEditModal(false)}
            style={{ background: "none", border: "none", fontSize: "20px", cursor: "pointer", color: "#64748b" }}
          >
            &times;
          </button>
        </div>

        <form onSubmit={handleEditAd}>
          <div style={{ marginBottom: "12px" }}>
            <label style={{ display: "block", fontWeight: "600", fontSize: "13px", marginBottom: "4px" }}>
              Ad Title *
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
              style={{ width: "100%", padding: "8px 12px", borderRadius: "6px", border: "1px solid #cbd5e1", boxSizing: "border-box" }}
            />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "12px" }}>
            <div>
              <label style={{ display: "block", fontWeight: "600", fontSize: "13px", marginBottom: "4px" }}>
                Category
              </label>
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                style={{ width: "100%", padding: "8px 12px", borderRadius: "6px", border: "1px solid #cbd5e1", boxSizing: "border-box" }}
              >
                <option value="General Notice">General Notice</option>
                <option value="Auctions">Auctions</option>
                <option value="Expressions of Interest">Expressions of Interest</option>
                <option value="Public Notices">Public Notices</option>
                <option value="Recruitment Notice">Recruitment Notice</option>
                <option value="Workshop / Conference">Workshop / Conference</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div>
              <label style={{ display: "block", fontWeight: "600", fontSize: "13px", marginBottom: "4px" }}>
                Display Status
              </label>
              <select
                name="display"
                value={formData.display}
                onChange={handleChange}
                style={{ width: "100%", padding: "8px 12px", borderRadius: "6px", border: "1px solid #cbd5e1", boxSizing: "border-box" }}
              >
                <option value="yes">Visible / Active</option>
                <option value="no">Hidden</option>
              </select>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "12px" }}>
            <div>
              <label style={{ display: "block", fontWeight: "600", fontSize: "13px", marginBottom: "4px" }}>
                Date
              </label>
              <input
                type="date"
                name="date"
                value={formData.date}
                onChange={handleChange}
                style={{ width: "100%", padding: "8px 12px", borderRadius: "6px", border: "1px solid #cbd5e1", boxSizing: "border-box" }}
              />
            </div>

            <div>
              <label style={{ display: "block", fontWeight: "600", fontSize: "13px", marginBottom: "4px" }}>
                Expiry / Last Date
              </label>
              <input
                type="date"
                name="expiryDate"
                value={formData.expiryDate}
                onChange={handleChange}
                style={{ width: "100%", padding: "8px 12px", borderRadius: "6px", border: "1px solid #cbd5e1", boxSizing: "border-box" }}
              />
            </div>
          </div>

          <div style={{ marginBottom: "12px" }}>
            <label style={{ display: "block", fontWeight: "600", fontSize: "13px", marginBottom: "4px" }}>
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={3}
              style={{ width: "100%", padding: "8px 12px", borderRadius: "6px", border: "1px solid #cbd5e1", boxSizing: "border-box" }}
            />
          </div>

          <div style={{ marginBottom: "16px" }}>
            <label style={{ display: "block", fontWeight: "600", fontSize: "13px", marginBottom: "4px" }}>
              Replace Image / PDF Ad (Optional)
            </label>
            <input
              type="file"
              onChange={handleFileChange}
              accept=".pdf,.jpg,.jpeg,.png,.webp"
              style={{ width: "100%", padding: "8px 12px", borderRadius: "6px", border: "1px solid #cbd5e1", boxSizing: "border-box" }}
            />
            {formData.file && !adFile && (
              <div style={{ marginTop: "8px" }}>
                <p style={{ margin: "0 0 4px", fontSize: "12px", color: "#64748b" }}>Current File:</p>
                <FilePreview url={formData.file} alt={formData.title} style={{ width: "100%", height: "100px" }} />
              </div>
            )}
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
            <button
              type="button"
              onClick={() => setShowEditModal(false)}
              style={{ padding: "10px 16px", background: "#f1f5f9", color: "#334155", border: "1px solid #cbd5e1", borderRadius: "6px", cursor: "pointer" }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={buttonLoading}
              style={{ padding: "10px 24px", background: "#3b82f6", color: "#fff", border: "none", borderRadius: "6px", cursor: "pointer", fontWeight: 600 }}
            >
              {buttonLoading ? <CircularProgress size={20} color="inherit" /> : "Save Changes"}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
